import { useEffect, useState } from "react";
import { Grade, GradeCreateRequest } from "../types";
import {
  createGrade,
  fetchGrades,
  removeGradeSection,
  replaceGradeSections,
} from "../services/gradesService";

// The backend sends conflict/not-found reasons (e.g. "Grade exists with code: X") as a plain-text body.
const messageOf = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

export const useGrades = () => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchGrades();
        setGrades(data);
      } catch (err) {
        setError("Failed to load grades.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isDuplicate = (field: "code" | "name", value: string) => {
    const normalized = value.trim().toLowerCase();
    return grades.some((grade) => (grade[field] ?? "").trim().toLowerCase() === normalized);
  };

  /** Returns null on success, or the reason it failed. */
  const create = async (request: GradeCreateRequest): Promise<string | null> => {
    setSaving(true);
    try {
      const created = await createGrade(request);
      setGrades((prev) => [...prev, created]);
      return null;
    } catch (err) {
      return messageOf(err, "Failed to create grade.");
    } finally {
      setSaving(false);
    }
  };

  /** Makes the grade's sections exactly `sectionCodes`. Returns null on success, or the reason it failed. */
  const saveSections = async (gradeCode: string, sectionCodes: string[]): Promise<string | null> => {
    setSaving(true);
    try {
      const sections = await replaceGradeSections(gradeCode, sectionCodes);
      setGrades((prev) => prev.map((grade) => (grade.code === gradeCode ? { ...grade, sections } : grade)));
      return null;
    } catch (err) {
      return messageOf(err, "Failed to update sections.");
    } finally {
      setSaving(false);
    }
  };

  /** Removes one section from the grade. Returns null on success, or the reason it failed. */
  const removeSection = async (gradeCode: string, sectionCode: string): Promise<string | null> => {
    setSaving(true);
    try {
      await removeGradeSection(gradeCode, sectionCode);
      setGrades((prev) =>
        prev.map((grade) =>
          grade.code === gradeCode
            ? { ...grade, sections: grade.sections.filter((section) => section.code !== sectionCode) }
            : grade
        )
      );
      return null;
    } catch (err) {
      return messageOf(err, "Failed to remove section.");
    } finally {
      setSaving(false);
    }
  };

  return {
    grades,
    loading,
    saving,
    error,
    isDuplicate,
    actions: {
      create,
      saveSections,
      removeSection,
    },
  };
};
